import type { VodAsset } from '../../data/asset';
import { convertMediaTypeToRhinoAssetType } from '../../data/compat/rhinoAssetTypeToMediaType';
import { tvpil } from '../../data/compat/tvpil';
import type { RentalEpisodeAsset, RentalMovieAsset } from '../../data/rental/types';
import { EntitledTvod, IVod } from '../objects';
import { fromKalturaBoolean } from '../utils';

import { processMediaImages, processVodAssetMetas, processEpisodeMetas, getRating } from './asset';

export type RequiredEntitledTvod = EntitledTvod & {
  vod: NonNullable<EntitledTvod['vod']>;
};

const processors = {
  movie: processMovie,
  episode: processEpisode,
};

export function processTvod(tvod: RequiredEntitledTvod) {
  const type = convertMediaTypeToRhinoAssetType(tvpil.dms.mediaTypesValue[tvod.vod.type]);

  const process = processors[type as keyof typeof processors] ?? processMovie;

  return process(tvod.vod);
}

function processVod(vod: IVod): VodAsset {
  return {
    ...processVodAssetMetas(vod.metadata),
    id: vod.id,
    title: vod.name,
    ...(vod.metadata?.['ParentalRating'] && {
      parentalRating: (vod.metadata?.['ParentalRating'] as number[])?.[0],
    }),
    isAdult: fromKalturaBoolean(vod.metadata?.['Adult']),
    images: processMediaImages(vod.images),
    type: convertMediaTypeToRhinoAssetType(tvpil.dms.mediaTypesValue[vod.type]),
  };
}

function processMovie(vod: IVod): RentalMovieAsset {
  return {
    ...processVod(vod),
    rating: getRating(vod.metadata),
    type: 'movie',
  };
}

function processEpisode(vod: IVod): RentalEpisodeAsset {
  return {
    ...processVod(vod),
    ...processEpisodeMetas(vod.metadata),
    rating: getRating(vod.metadata),
    type: 'episode',
  };
}
